import { HttpClient } from "helpers";
import {
  BaseResponseType,
  DataGridRequest,
  FileUploadResponse,
  PagedResponseType,
  PagedResult,
} from "services/shared-models/sharedTypes";
import {
  ConditionsAndRulesByIDResponse,
  ConditionsAndRulesResultResponse,
  PDFTemplateByIDResponse,
  PDFTemplatesResultResponse,
  ProgramLocalItemResult,
  ProgramByIDResponse,
  ProgramLocalResult,
  CreateProgramMinutes,
  ProgramActionResult,
  WorkflowDesignProcessLocalResult,
  ProgramStatisticsResult,
  ProgramStatisticsCountResult,
  RejectionReasonResult,
  DashboardFilterRequest,
  DashboardSummaryResult,
  MonthlyApplicationsChartResult,
  ApplicationsByCollegeChartResult,
  ApplicationsByRankAndGenderChartResult,
  ApplicationsByGenderChartResult,
  ApplicationsByDepartmentChartResult,
  ResearchGroupsDashboardFilterRequest,
  ResearchGroupsSummaryResult,
  ApplicationsByFacultyChartResult,
  ApplicationsByLocalClassificationChartResult,
  RejectionReasonsChartResult,
  ApplicationDetailsByStatementResult,
} from "./models/ProgramTypes";
import { CreateProgramRequest } from "views/home/programs/Forms/program.schema";
import { AxiosResponse } from "axios";

export const ProgramsService = {
  createProgram: async (data: CreateProgramRequest) => {
    return await HttpClient.post<BaseResponseType<any>>("/api/Programs/CreateProgram", data);
  },
  updateProgram: async (id: number, data: CreateProgramRequest) => {
    return await HttpClient.put<BaseResponseType<any>>(`/api/Programs/UpdateProgram/${id}`, data);
  },
  getAllProgramsAsync: async (data: DataGridRequest) => {
    return await HttpClient.post<PagedResponseType<ProgramLocalResult>>("/api/Programs/GetAllPrograms", data);
  },
  getProgramById: async (id: number) => {
    return await HttpClient.get<BaseResponseType<ProgramByIDResponse>>(`/api/Programs/GetProgramById/${id}`);
  },
  getAllConditionsAndRules: async (request: { search: string; pageIndex: number; pageSize: number }) => {
    return HttpClient.post<PagedResponseType<ConditionsAndRulesResultResponse[]>>(
      "/api/Programs/GetAllConditionsAndRules",
      request,
    );
  },
  getAllPdfTemplates: async (request: { search: string; pageIndex: number; pageSize: number }) => {
    return HttpClient.post<PagedResponseType<PDFTemplatesResultResponse>>("/api/Programs/GetAllPdfTemplates", request);
  },
  getConditionAndRuleById: async (id: number) => {
    return HttpClient.get<BaseResponseType<ConditionsAndRulesByIDResponse>>(
      `/api/Programs/GetConditionAndRuleById/${id}`,
    );
  },
  getPdfTemplateById: async (id: number) => {
    return HttpClient.get<BaseResponseType<PDFTemplateByIDResponse>>(`/api/Programs/GetPdfTemplateById/${id}`);
  },
  fileUpload: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return HttpClient.post<BaseResponseType<FileUploadResponse>>("/api/Programs/FileUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getAllPublicPrograms: async (data: DataGridRequest) => {
    return HttpClient.post<PagedResponseType<ProgramLocalResult>>(`/api/Programs/GetAllPublicPrograms`, data);
  },
  getPublicProgramById: async (id: number) => {
    return HttpClient.get<BaseResponseType<ProgramLocalItemResult>>(`/api/Programs/GetPublicProgramById/${id}`);
  },
  deleteProgram: async (id: number) => {
    return HttpClient.delete<BaseResponseType<ProgramLocalItemResult>>(`/api/Programs/DeleteProgram/${id}`);
  },
  exportProgramsAsync: async (data: DataGridRequest) => {
    return HttpClient.post<AxiosResponse<Blob>>(`/api/Programs/ExportProgramsToExcel`, data, { responseType: "blob" });
  },
  sendFundingUnitRequest: async (programId: number) => {
    return HttpClient.put<BaseResponseType<ProgramLocalItemResult>>(
      `/api/Programs/SendFundingUnitRequest/${programId}`,
    );
  },
  updateProgramMinutes: async (programMinutesId: number, data: CreateProgramMinutes) => {
    return await HttpClient.put<BaseResponseType<ProgramLocalItemResult>>(
      `/api/Programs/UpdateProgramMinutes/${programMinutesId}`,
      data,
    );
  },
  getAllProgramAction: async (programId: number) => {
    return await HttpClient.get<BaseResponseType<PagedResult<ProgramActionResult>>>(
      `/api/Programs/GatAllProgramAction/${programId}`,
    );
  },
  getAllWorkflowProcess: async (request: DataGridRequest) => {
    return await HttpClient.post<BaseResponseType<PagedResult<PagedResult<WorkflowDesignProcessLocalResult>>>>(
      `/api/Programs/GetAllWorkflowProcess`,
      request,
    );
  },
  getProgramStatistics: async () => {
    return await HttpClient.get<BaseResponseType<ProgramStatisticsResult[]>>("/api/Programs/GetProgramStatistics");
  },
  getProgramStatisticsCount: async () => {
    return await HttpClient.get<BaseResponseType<ProgramStatisticsCountResult>>("/api/Programs/GetProgramStatisticsCount");
  },
  getRejectionReasonsByProgramId: async (programId: number) => {
    return await HttpClient.get<BaseResponseType<RejectionReasonResult[]>>(
      `/api/Programs/GetRejectionReasonsByProgramId/${programId}`
    );
  },
  // Dashboard APIs
  getDashboardSummary: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<DashboardSummaryResult>>(
      "/api/Programs/GetDashboardSummary",
      request
    );
  },
  getMonthlyApplicationsChart: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<MonthlyApplicationsChartResult[]>>(
      "/api/Programs/GetMonthlyApplicationsChart",
      request
    );
  },
  getApplicationsByCollegeChart: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByCollegeChartResult[]>>(
      "/api/Programs/GetApplicationsByCollegeChart",
      request
    );
  },
  getApplicationsByRankAndGenderChart: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByRankAndGenderChartResult[]>>(
      "/api/Programs/GetApplicationsByRankAndGenderChart",
      request
    );
  },
  getApplicationsByGenderChart: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByGenderChartResult[]>>(
      "/api/Programs/GetApplicationsByGenderChart",
      request
    );
  },
  getApplicationsByDepartmentChart: async (request: DashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByDepartmentChartResult[]>>(
      "/api/Programs/GetApplicationsByDepartmentChart",
      request
    );
  },
  // Research Groups Dashboard APIs
  getResearchGroupsSummary: async (request: ResearchGroupsDashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ResearchGroupsSummaryResult>>(
      "/api/Programs/GetResearchGroupsSummary",
      request
    );
  },
  getApplicationsByFacultyChart: async (request: ResearchGroupsDashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByFacultyChartResult[]>>(
      "/api/Programs/GetApplicationsByFacultyChart",
      request
    );
  },
  getApplicationsByLocalClassificationChart: async (request: ResearchGroupsDashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationsByLocalClassificationChartResult[]>>(
      "/api/Programs/GetApplicationsByLocalClassificationChart",
      request
    );
  },
  getRejectionReasonsChart: async (request: ResearchGroupsDashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<RejectionReasonsChartResult[]>>(
      "/api/Programs/GetRejectionReasonsChart",
      request
    );
  },
  getApplicationDetailsByStatement: async (request: ResearchGroupsDashboardFilterRequest) => {
    return await HttpClient.post<BaseResponseType<ApplicationDetailsByStatementResult[]>>(
      "/api/Programs/GetApplicationDetailsByStatement",
      request
    );
  },
};
